import React, { FC, useCallback, useState, useMemo } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import { Box, Stack, Divider, StackProps } from '@mui/material';
import { IntlShape } from 'react-intl';

import { ProjectButton } from './ProjectButton';
import { ProjectsListCollapseButton } from './ProjectsListCollapseButton';
import * as styles from './ProjectsListDialogView.styles';

import { SearchableProjectsList } from './SearchableProjectsList';

interface ProjectsListDialogViewProps extends StackProps {
  projects: ProjectModel[];
  selectedProjectId?: number | null;
  onProjectButtonClick?: (project: ProjectModel) => void;
  intl: IntlShape;
}

export const ProjectsListDialogView: FC<ProjectsListDialogViewProps> = (
  props,
) => {
  const {
    projects,
    selectedProjectId,
    onProjectButtonClick,
    intl,
    ...stackProps
  } = props;

  const [collapsed, setCollapsed] = useState(true);

  const lastProjectsLists = useMemo(() => projects.slice(0, 3), [projects]);
  const restProjectsLists = useMemo(() => projects.slice(3), [projects]);

  const displaySeeMoreButton = !!restProjectsLists.length;

  const onProjectListCollapse = useCallback(() => {
    setCollapsed((prevCollapsed) => !prevCollapsed);
  }, []);

  return (
    <Stack {...stackProps}>
      <Box sx={styles.projectsList}>
        {lastProjectsLists.map((project) => (
          <ProjectButton
            key={project.id}
            project={project}
            selected={selectedProjectId === project.externalId}
            onClick={onProjectButtonClick}
          >
            {project.title}
          </ProjectButton>
        ))}
        {displaySeeMoreButton && (
          <ProjectsListCollapseButton
            collapsed={collapsed}
            onClick={onProjectListCollapse}
            intl={intl}
          />
        )}
      </Box>
      <Divider sx={styles.divider} />
      {!collapsed && (
        <SearchableProjectsList
          selectedProjectId={selectedProjectId}
          projects={restProjectsLists}
          onProjectButtonClick={onProjectButtonClick}
          intl={intl}
        />
      )}
    </Stack>
  );
};
